import { useStore } from "hooks/use-store";
import { SettingsSection } from "../components/settings-section";
import { Button } from "components/ui/button";
import { Color } from "utils/theme";
import { useRef, useState } from "react";
import useMqttApi from "hooks/use-mqtt-api";
import { RequestTopics } from "services/topics";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { TabContainer } from "../components/tab-container";
import { ScrollButtons } from "components/ui/scroll-buttons";
import { NumberInput } from "components/ui/number-input";
import { Spinner } from "components/ui/spinner";
import { LockIcon } from "lucide-react";

const SUPPORT_EMAIL = "support@hefringmarine.com";

interface SystemSettingsErrors {
  impactLimit?: {
    message: string;
  };
  speedLimit?: {
    message: string;
  };
  boatLength?: {
    message: string;
  };
}

export const SystemSettings = ({ title }) => {
  const { t } = useTranslation();

  const config = useStore((state) => state.config);
  const setConfig = useStore((state) => state.setConfig);

  const [errors, setErrors] = useState<SystemSettingsErrors>({});
  const [impactLimit, setImpactLimit] = useState(config?.max_impact);
  const [speedLimit, setSpeedLimit] = useState(config?.max_speed);
  const [boatLength, setBoatLength] = useState(config?.boat_length);

  const ref = useRef<HTMLDivElement>(null);

  const { fetch: fetchConfig } = useMqttApi({
    requestTopic: RequestTopics.Config,
    timeout: false,
    enabled: false,
    onSuccess: (data) => {
      setConfig(data.data);
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const { fetch: updateSystemSettings, loading } = useMqttApi({
    requestTopic: RequestTopics.ThresholdConfig,
    request: {
      max_impact: impactLimit ?? null,
      max_speed: speedLimit ?? null,
      boat_length: boatLength ?? null,
    },
    enabled: false,
    onError: (error) => {
      toast.error(error?.message);
    },
    onSuccess: () => {
      toast.success(t("settings.system_settings.success_message"));
      fetchConfig();
    },
  });

  const onSubmit = () => {
    const newErrors: SystemSettingsErrors = {};

    if (impactLimit && (isNaN(impactLimit) || Number(impactLimit) < 0)) {
      newErrors.impactLimit = {
        message: t("settings.system_settings.error_messages.impact_limit"),
      };
    }

    if (speedLimit && (isNaN(speedLimit) || Number(speedLimit) < 0)) {
      newErrors.impactLimit = {
        message: t("settings.system_settings.error_messages.speed_limit"),
      };
    }

    if (boatLength && (isNaN(boatLength) || Number(boatLength) < 0)) {
      newErrors.boatLength = {
        message: t("settings.system_settings.error_messages.boat_length"),
      };
    }

    if (Object.keys(newErrors).length === 0) {
      updateSystemSettings({
        max_impact: impactLimit,
        max_speed: speedLimit,
        boat_length: boatLength,
      });
      setErrors({});
    } else {
      setErrors(newErrors);
    }
  };

  if (!config?.has_threshold_overwrite) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center text-center gap-3 max-w-md p-5">
          <div
            className="rounded-full p-4"
            style={{ backgroundColor: Color.Primary[700] }}
          >
            <LockIcon
              className="size-8"
              style={{ color: Color.Primary[300] }}
            />
          </div>
          <h3
            className="text-lg font-semibold"
            style={{ color: Color.Primary[200] }}
          >
            {t("settings.system_settings.not_available.title")}
          </h3>
          <p style={{ color: Color.Primary[400] }}>
            {t("settings.system_settings.not_available.description")}
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="underline"
            style={{ color: Color.Indigo[400] }}
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </div>
    );
  }

  return (
    <TabContainer ref={ref} title={title}>
      <ScrollButtons containerRef={ref} className="absolute bottom-4 right-4" />
      <section className="space-y-2">
        <SettingsSection
          label={t("settings.system_settings.impact_limit.title")}
          description={t("settings.system_settings.impact_limit.description")}
          content={
            <NumberInput
              name="impactLimit"
              defaultValue={impactLimit}
              step={1}
              min={0}
              max={100}
              onValueChange={(value) => setImpactLimit(value)}
              error={errors?.impactLimit}
            />
          }
        />
        <SettingsSection
          label={t("settings.system_settings.speed_limit.title")}
          description={t("settings.system_settings.speed_limit.description")}
          content={
            <NumberInput
              name="speedLimit"
              defaultValue={speedLimit}
              type="number"
              step={1}
              min={0}
              max={200}
              onValueChange={(value) => setSpeedLimit(value)}
              error={errors?.speedLimit}
            />
          }
        />
        <SettingsSection
          label={t("settings.system_settings.boat_length.title")}
          description={t("settings.system_settings.boat_length.description")}
          content={
            <NumberInput
              name="boatLength"
              defaultValue={boatLength}
              step={1}
              min={0}
              max={200}
              onValueChange={(value) => setBoatLength(value)}
              error={errors?.boatLength}
            />
          }
        />
      </section>
      <Button
        color={Color.Indigo[500]}
        className="mt-4 float-right w-full sm:w-[160px]"
        onClick={() => onSubmit()}
        disabled={loading}
      >
        {loading ? <Spinner className="size-5 mr-3" /> : null}
        {t("common.save")}
      </Button>
    </TabContainer>
  );
};
